import { useId, useMemo } from "react";
import type { Expression } from "../types";
import { EXPRESSION_WORDS } from "../format";
import { ART_SIZE, portraitMarkup } from "./art";

/**
 * Layered SVG portrait (16 §6, 19 §8.3, 22_art_brief.md). Layers, back to front:
 * backdrop → hair (back) → neck → clothing → ears → head → age lines → under-eye →
 * brows, eyes, mouth (the expression) → facial hair → glasses → hair (front).
 * Only the expression layers (and the under-eye shading) change when the expression changes.
 */

export interface PortraitProps {
  /** Drawn cast portrait key, if any (e.g. "maya"); otherwise the face is assembled from the parts kit. */
  portraitId: string | null | undefined;
  /** Seed for generated people; use the person id so they always look the same. */
  seed: string;
  name: string;
  role?: string | undefined;
  grade?: string | undefined;
  expression?: Expression | undefined;
  /**
   * Strain the player can see (perceived, never a hidden value): adds the tired shading under a
   * neutral or pleased face, so a smile can still look exhausted (22 §4.3).
   */
  strain?: boolean | undefined;
  size?: number | undefined;
  className?: string | undefined;
  /** Decorative copies (e.g. beside a name already announced) can be hidden from assistive tech. */
  decorative?: boolean | undefined;
}

export function portraitLabel(name: string, role: string | undefined, expression: Expression | undefined, strain?: boolean): string {
  const who = role ? `${name}, ${role}.` : `${name}.`;
  if (!expression) return who;
  const words = EXPRESSION_WORDS[expression] ?? EXPRESSION_WORDS.neutral;
  const tired = strain && (expression === "neutral" || expression === "pleased") ? " Tired around the eyes." : "";
  return `${who} ${words.charAt(0).toUpperCase()}${words.slice(1)}.${tired}`;
}

const escape = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function Portrait(props: PortraitProps) {
  const { portraitId, seed, name, role, grade, expression = "neutral", strain, size = 96, className, decorative } = props;
  const uid = `pt${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const label = portraitLabel(name, role, expression, strain);
  const html = useMemo(() => {
    const art = portraitMarkup({ portraitId, seed, grade, expression, strain, uid });
    const title = decorative ? "" : `<title>${escape(label)}</title>`;
    return `${title}<rect class="portrait-bg" width="${ART_SIZE}" height="${ART_SIZE}"/><g class="portrait-face">${art}</g>`;
  }, [portraitId, seed, grade, expression, strain, uid, decorative, label]);
  const cls = ["portrait", className].filter(Boolean).join(" ");
  const a11y = decorative ? { "aria-hidden": true as const } : { role: "img", "aria-label": label };
  return (
    <svg
      className={cls}
      viewBox={`0 0 ${ART_SIZE} ${ART_SIZE}`}
      width={size}
      height={size}
      data-expression={expression}
      data-strain={strain && (expression === "neutral" || expression === "pleased") ? "true" : undefined}
      focusable="false"
      {...a11y}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
